import type { ReactNode } from 'react';
import { Children } from 'react';

interface Props {
  children?: ReactNode;
}

/**
 * Vertical stack for top-of-pane status banners (error, outdated-version,
 * linked-draft/not-linked, ask-to-link, pin-add-in tooltip).
 *
 * Previously each banner positioned itself via `absolute top-12 left-0
 * right-0 z-20`, so when 2+ were mounted simultaneously they overlapped at
 * the same y-coordinate AND covered the chat scroll area underneath. This
 * component renders them as a normal-flow vertical column between the
 * Header and the chat-citation row, so they stack naturally and the chat
 * scrolls below whatever banners are present.
 *
 * Order of children controls visual order top→bottom. Renders nothing when
 * no children are mounted (no extra spacing when the stack is empty).
 */
export function BannerStack({ children }: Props): JSX.Element | null {
  // Filter out falsy children (each banner is mounted via a conditional, so
  // unmounted slots appear as `false`/`null`). When everything is unmounted
  // the stack collapses entirely — no border, no spacing.
  const mounted = Children.toArray(children).filter(Boolean);
  if (mounted.length === 0) return null;

  return (
    <div className="flex flex-col flex-shrink-0">
      {mounted}
    </div>
  );
}
